import { useCallback, useMemo, useState } from 'react'
import { Outlet } from 'react-router-dom'
import { importApi } from '../api/imports'
import type { EntityDef } from '../api/types'
import { useCompanies } from '../companies/useCompanies'
import { ErrorState, LoadingState } from '../components/States'
import { useAsync } from '../hooks/useAsync'
import { DATE_PRESETS, ExportWizardContext } from './ExportWizardContext'
import type { DateMode, ExportWizard } from './ExportWizardContext'

/**
 * Holds the export wizard's choices between steps. Nothing here is persisted;
 * the final step submits them to `POST /api/export/submit`, which queues a job
 * that queries QuickBooks and writes the result out as a CSV.
 *
 * The entity list comes from `GET /api/import/entities`. There is no export
 * entities endpoint, and that one already carries the `isTransaction` flag the
 * Lists / Transactions toggle needs. The consequence is that export-only
 * subjects the old hardcoded list had — Profit & Loss, Balance Sheet, Trial
 * Balance, AR/AP ageing — are not offered here; they would need either an
 * export entities endpoint or a reports endpoint of their own.
 */
export function ExportWizardProvider() {
  const entities = useAsync<EntityDef[]>(() => importApi.entities(), [])
  const { current } = useCompanies()
  const [entityType, setEntityTypeState] = useState('')
  const [reference, setReference] = useState('')
  const [dateMode, setDateMode] = useState<DateMode>('preset')
  const [preset, setPreset] = useState(DATE_PRESETS[0])
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')

  // Changing the subject invalidates the filters that were scoped to it.
  const setEntityType = useCallback((name: string) => {
    setEntityTypeState(name)
    setReference('')
  }, [])

  const reset = useCallback(() => {
    setEntityTypeState('')
    setReference('')
    setDateMode('preset')
    setPreset(DATE_PRESETS[0])
    setFrom('')
    setTo('')
  }, [])

  const list = useMemo(() => entities.data ?? [], [entities.data])
  const value = useMemo<ExportWizard>(
    () => ({
      entities: list,
      entityType,
      entity: list.find((e) => e.name === entityType) ?? null,
      companyId: current?.id ?? null,
      reference,
      dateMode,
      preset,
      from,
      to,
      setEntityType,
      setReference,
      setDateMode,
      setPreset,
      setFrom,
      setTo,
      reset,
    }),
    [list, entityType, current?.id, reference, dateMode, preset, from, to, setEntityType, reset],
  )

  if (entities.loading) return <LoadingState label="Loading export types…" />
  if (entities.error) return <ErrorState error={entities.error} onRetry={entities.reload} />

  return (
    <ExportWizardContext.Provider value={value}>
      <Outlet />
    </ExportWizardContext.Provider>
  )
}
